import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../../contexts/ToastContext';

const IntakeVerify: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showError, showSuccess } = useToast();
  const [isVerifying, setIsVerifying] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verifyToken = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setError('No verification token provided');
        setIsVerifying(false);
        return;
      }

      try {
        const response = await fetch('/api/auth/magic-link/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token })
        });

        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.message || 'Verification failed');
        }

        const response_data = await response.json();
        const data = response_data.data; // Extract data from standardized response wrapper
        
        // The session cookie should be set by the API response
        // BetterAuth handles session management via cookies automatically
        
        showSuccess('Successfully verified! Redirecting...');
        
        // Redirect based on returnUrl
        const redirectUrl = data.returnUrl || '/intake-form';
        
        // Use window.location for a full page reload to ensure cookies are set
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 1000);
      } catch (error) {
        console.error('Verification error:', error);
        setError(error instanceof Error ? error.message : 'Verification failed');
        showError('Verification failed. Please request a new link.');
      } finally {
        setIsVerifying(false);
      }
    };

    verifyToken();
  }, [searchParams, navigate, showError, showSuccess]);

  if (isVerifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-700">Verifying your access...</h2>
          <p className="text-gray-500 mt-2">Please wait while we verify your link</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full bg-white rounded-lg shadow-sm p-8 text-center">
          <div className="mb-6">
            <svg className="mx-auto h-16 w-16 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-2xl font-semibold mb-4">Verification Failed</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button
            onClick={() => navigate('/intake')}
            className="px-6 py-2 bg-primary text-white rounded-lg font-medium hover:bg-primary-dark transition-colors"
          >
            Request New Link
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default IntakeVerify;